import type { ReactNode } from "react";
import { Section } from "./Section";
import { ReadinessRing } from "./ReadinessRing";
import { PdfAccent } from "./PdfAccent";
import { PdfChip } from "./PdfChip";
import {
  ArrowUpRight,
  ArrowDownRight,
  Check,
  Clock,
  Sparkles,
  Target,
  TrendingUp,
  ShieldAlert,
  Activity,
  Zap,
  Compass,
  Database,
  Workflow,
  Users,
  Cpu,
} from "lucide-react";
import type {
  AssessmentSignalInputsSection,
  ExecutionPhase,
  OpportunityCard,
  RecommendedImmediateActionItem,
  ReportCopy,
  StrategicRiskItem,
} from "./reportTypes";

type SectionVariant = "screen" | "pdf";

function Accent({ variant, children }: { variant?: SectionVariant; children: ReactNode }) {
  if (variant === "pdf") {
    return <PdfAccent>{children}</PdfAccent>;
  }
  return <span className="text-gradient">{children}</span>;
}

/* ───────────────────────── snapshot ───────────────────────── */

export type SnapshotProps = {
  overallScore: number;
  readiness: string;
  fastestRoiArea: string;
  pilotReadiness: string;
  opportunityWindow: string;
  executivePhrase: string;
  benchmarkCopy: string;
  bulletSignals: string[];
  variant?: SectionVariant;
  stacked?: boolean;
};

export function Snapshot({
  overallScore,
  readiness,
  fastestRoiArea,
  pilotReadiness,
  opportunityWindow,
  executivePhrase,
  benchmarkCopy,
  bulletSignals,
  variant = "screen",
  stacked = false,
}: SnapshotProps) {
  const metrics = [
    { label: "AI Readiness", value: readiness, icon: Activity },
    { label: "Fastest ROI Area", value: fastestRoiArea, icon: Zap },
    { label: "Pilot Readiness", value: pilotReadiness, icon: Target },
    { label: "Opportunity Window", value: opportunityWindow, icon: Clock },
  ];

  return (
    <Section
      id="snapshot"
      index="01"
      eyebrow="Executive Signal Snapshot"
      title={
        <>
          Summary assessment of <Accent variant={variant}>AI readiness</Accent>.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      {variant === "pdf" ? (
        <div className="pdf-snapshot-layout">
          <div className="pdf-snapshot-layout__hero">
            <div className="pdf-snapshot-layout__ring">
              <ReadinessRing value={overallScore} readinessLabel={readiness} compact />
            </div>
            <div className="pdf-snapshot-layout__side">
              <div className="card-premium">
                <PdfChip size="xs">Executive Interpretation</PdfChip>
                <p className="mt-2 font-display text-[13px] leading-snug">
                  {executivePhrase ? (
                    <>
                      Your organization is <Accent variant={variant}>{executivePhrase}</Accent>.
                    </>
                  ) : null}
                </p>
                <ul className="mt-2 space-y-1 text-[11px] text-foreground/70">
                  {bulletSignals.slice(0, 3).map((s) => (
                    <li key={s} className="flex items-center gap-1.5">
                      <Check className="h-3 w-3 text-[var(--electric)] shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-premium flex items-center gap-3">
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                  style={{ background: "var(--gradient-signal)" }}
                >
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] uppercase tracking-[0.16em] text-foreground/55">Benchmark</p>
                  <p className="font-display text-[12px] font-medium leading-snug">{benchmarkCopy}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="pdf-snapshot-metrics">
            {metrics.map((m) => (
              <div key={m.label} className="card-premium">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[8px] uppercase tracking-[0.14em] text-foreground/55 leading-tight">{m.label}</span>
                  <m.icon className="h-3.5 w-3.5 shrink-0 text-[var(--electric)]" />
                </div>
                <p className="mt-1.5 font-display text-sm font-medium leading-tight">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center pdf-snapshot-grid">
        <div className="lg:col-span-3 flex justify-center">
          <ReadinessRing value={overallScore} readinessLabel={readiness} />
        </div>
        <div className="lg:col-span-2 space-y-4">
          <div className="card-premium p-7">
            <span className="chip">Executive Interpretation</span>
            <p className="mt-4 font-display text-xl leading-snug">
              {executivePhrase ? (
                <>
                  Your organization is <Accent variant={variant}>{executivePhrase}</Accent>.
                </>
              ) : null}
            </p>
            <ul className="mt-4 space-y-2 text-[14px] text-foreground/70">
              {bulletSignals.map((s) => (
                <li key={s} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[var(--electric)]" /> {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="card-premium p-6 flex items-center gap-4">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-full"
              style={{ background: "var(--gradient-signal)" }}
            >
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-foreground/55">Benchmark</p>
              <p className="font-display font-medium">{benchmarkCopy}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 pdf-snapshot-metrics">
        {metrics.map((m) => (
          <div key={m.label} className="card-premium p-5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.18em] text-foreground/55">{m.label}</span>
              <m.icon className="h-4 w-4 text-[var(--electric)]" />
            </div>
            <p className="mt-3 font-display text-lg font-medium">{m.value}</p>
          </div>
        ))}
      </div>
        </>
      )}
    </Section>
  );
}

/* ───────────────────────── signal breakdown ───────────────────────── */

export type SignalBreakdownProps = {
  scores: {
    data: number;
    process: number;
    people: number;
    technology: number;
    strategy: number;
  };
  notes: {
    data: string;
    process: string;
    people: string;
    technology: string;
    strategy: string;
  };
  variant?: SectionVariant;
  stacked?: boolean;
};

export function SignalBreakdown({
  scores,
  notes,
  variant = "screen",
  stacked = false,
}: SignalBreakdownProps) {
  const signals = [
    { label: "Data Signal", value: scores.data, note: notes.data },
    { label: "Process Signal", value: scores.process, note: notes.process },
    { label: "People Signal", value: scores.people, note: notes.people },
    { label: "Technology Signal", value: scores.technology, note: notes.technology },
    { label: "Strategy Signal", value: scores.strategy, note: notes.strategy },
  ];

  return (
    <Section
      id="signals"
      index="02"
      eyebrow="Signal Breakdown"
      title={
        <>
          Pillar scores and <Accent variant={variant}>assessment</Accent> results.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      {variant === "pdf" ? (
        <div className="card-premium">
          <div className="pdf-signal-rows">
            {signals.map((s) => (
              <div key={s.label} className="pdf-signal-row">
                <p className="text-[9px] uppercase tracking-[0.14em] text-foreground/55">{s.label}</p>
                <div className="signal-bar">
                  <span style={{ width: `${s.value}%` }} />
                </div>
                <span className="font-display text-sm font-semibold tabular-nums text-right">{s.value}%</span>
                <p className="pdf-signal-row__note">{s.note}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
      <div className="card-premium p-8 md:p-10">
        <div className="space-y-7">
          {signals.map((s) => (
            <div key={s.label} className="grid grid-cols-1 md:grid-cols-12 items-center gap-4">
              <div className="md:col-span-3">
                <p className="text-[11px] uppercase tracking-[0.18em] text-foreground/55">{s.label}</p>
              </div>
              <div className="md:col-span-6">
                <div className="signal-bar">
                  <span style={{ width: `${s.value}%` }} />
                </div>
              </div>
              <div className="md:col-span-1 text-right">
                <span className="font-display font-semibold tabular-nums">{s.value}%</span>
              </div>
              <div className="md:col-span-2 text-[12px] text-foreground/60 leading-snug">{s.note}</div>
            </div>
          ))}
        </div>
      </div>
      )}
    </Section>
  );
}

/* ───────────────────────── interpretation ───────────────────────── */

export function Interpretation({
  interpretationCopy,
  variant = "screen",
  compactObservation = false,
  stacked = false,
}: {
  interpretationCopy?: ReportCopy["executive_interpretation"];
  variant?: SectionVariant;
  compactObservation?: boolean;
  stacked?: boolean;
}) {
  const fallbackCards = [
    { k: "01", h: "Readiness status.", p: "Readiness alone does not create return on investment. The next step is to define direction and measurable outcomes, not broad adoption." },
    { k: "02", h: "Primary strength.", p: "Leadership and operational openness to AI execution is uncommon in mid-market organizations and supports faster implementation." },
    { k: "03", h: "Primary gap.", p: "Operational data is not yet connected to measurable decision systems. Improved visibility is required before value can be captured at scale." },
  ];

  const cards = [
    { k: "01", h: "Readiness status.", p: interpretationCopy?.ai_ready_statement || fallbackCards[0].p },
    { k: "02", h: "Primary strength.", p: interpretationCopy?.advantage_statement || fallbackCards[1].p },
    { k: "03", h: "Primary gap.", p: interpretationCopy?.biggest_gap_statement || fallbackCards[2].p },
  ];

  const signalObservation =
    interpretationCopy?.signal_observation ||
    "Most organizations invest in AI tools before identifying where operational return is achievable. Higher-performing peers prioritize use cases with measurable business impact.";

  return (
    <Section
      id="interpretation"
      index="03"
      eyebrow="Executive Interpretation"
      title={
        <>
          Executive <Accent variant={variant}>interpretation</Accent>.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      <div className="report-interpretation-cards grid grid-cols-1 md:grid-cols-3 gap-5">
        {cards.map((c) => (
          <div key={c.k} className="card-premium p-7">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[var(--electric)] font-medium">{c.k}</span>
            <h3 className={`mt-3 font-display font-semibold ${variant === "pdf" ? "text-lg" : "text-xl"}`}>{c.h}</h3>
            <p className="mt-3 text-[14px] text-foreground/65 leading-relaxed">{c.p}</p>
          </div>
        ))}
      </div>

      {!stacked && (
      <div
        className={`report-signal-observation relative overflow-hidden rounded-2xl text-white ${
          compactObservation ? "mt-4 p-6" : "mt-8 p-8 md:p-10"
        }`}
        style={{ background: "var(--gradient-dark)" }}
      >
        <div
          className="absolute -right-24 -top-24 h-96 w-96 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(10,132,255,0.35), transparent 60%)" }}
        />
        <span className="chip chip-dark">Signal Observation</span>
        <p
          className={`mt-4 font-display leading-snug max-w-3xl ${
            compactObservation ? "text-xl" : "text-2xl md:text-3xl"
          }`}
        >
          {signalObservation}
        </p>
      </div>
      )}
    </Section>
  );
}

/* ───────────────────────── opportunities ───────────────────────── */

function Stat({
  k,
  v,
  highlight,
  variant = "screen",
}: {
  k: string;
  v: string;
  highlight?: boolean;
  variant?: SectionVariant;
}) {
  return (
    <div>
      <p className="uppercase tracking-[0.16em] text-foreground/45">{k}</p>
      <p className="mt-1 font-display font-semibold">
        {highlight ? <Accent variant={variant}>{v}</Accent> : v}
      </p>
    </div>
  );
}

const FALLBACK_OPPORTUNITY_CARDS: OpportunityCard[] = [
  {
    title: "Predictive Maintenance",
    recommended: true,
    metrics: [
      { direction: "down", value: "30%", label: "downtime" },
      { direction: "up", value: "uptime", label: "operational" },
      { direction: "down", value: "costs", label: "reactive maintenance" },
    ],
    why: "Existing sensor infrastructure and process maturity support a focused automation pilot.",
    complexity: "Medium",
    roi_window: "3–6 Months",
    signal: "HIGH",
  },
  {
    title: "AI-Assisted Customer Support",
    metrics: [
      { direction: "down", value: "delays", label: "response" },
      { direction: "up", value: "scale", label: "support" },
      { direction: "up", value: "CSAT", label: "satisfaction" },
    ],
    why: "Support volume and routing patterns indicate a practical automation opportunity.",
    complexity: "Low",
    roi_window: "2–4 Months",
    signal: "HIGH",
  },
  {
    title: "Financial Anomaly Detection",
    metrics: [
      { direction: "up", value: "visibility", label: "fraud" },
      { direction: "down", value: "leakage", label: "financial" },
      { direction: "up", value: "governance", label: "confidence" },
    ],
    why: "Transaction volume and audit processes support a controlled detection use case.",
    complexity: "Medium",
    roi_window: "4–6 Months",
    signal: "MEDIUM",
  },
];

export function Opportunities({
  cards,
  variant = "screen",
  stacked = false,
}: {
  cards?: OpportunityCard[];
  variant?: SectionVariant;
  stacked?: boolean;
}) {
  const renderedCards = cards?.length ? cards : FALLBACK_OPPORTUNITY_CARDS;

  return (
    <Section
      id="opportunities"
      index="04"
      eyebrow="Top AI Opportunities"
      title={
        <>
          Prioritized AI opportunities by <Accent variant={variant}>expected return</Accent>.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      <div
        className={
          variant === "pdf"
            ? "report-opportunities-grid pdf-opportunities-grid grid grid-cols-3 gap-2"
            : "report-opportunities-grid grid grid-cols-1 lg:grid-cols-3 gap-5"
        }
      >
        {renderedCards.map((c) => (
          <div
            key={c.title}
            className={`report-opportunity-card card-premium relative overflow-hidden ${c.recommended ? "glow-ring" : ""} ${variant === "pdf" ? "" : "p-7"}`}
          >
            {c.recommended &&
              (variant === "pdf" ? (
                <PdfChip size="xs" className="mb-2">
                  Recommended
                </PdfChip>
              ) : (
                <span className="absolute right-5 top-5 chip">Recommended</span>
              ))}
            <h3 className="report-opportunity-title font-display text-xl font-semibold leading-snug pr-20">{c.title}</h3>
            <div className="report-opportunity-metrics mt-5 space-y-2">
              {c.metrics?.map((m, i) => (
                <div key={i} className="flex items-center gap-2 text-[13px]">
                  {m.direction === "down" ? (
                    <ArrowDownRight className="h-4 w-4 text-[var(--electric)]" />
                  ) : (
                    <ArrowUpRight className="h-4 w-4 text-[var(--electric)]" />
                  )}
                  <span className="font-medium">{m.value}</span>
                  <span className="text-foreground/55">{m.label}</span>
                </div>
              ))}
            </div>
            <div className="my-5 divider-thin" />
            <p className="text-[12px] text-foreground/60 leading-relaxed">
              <span className="uppercase tracking-[0.18em] text-foreground/45 mr-1">Why</span>
              {c.why}
            </p>
            <div className="report-opportunity-stats mt-5 grid grid-cols-3 gap-3 text-[11px]">
              <Stat k="Complexity" v={c.complexity || ""} variant={variant} />
              <Stat k="ROI Window" v={c.roi_window || ""} variant={variant} />
              <Stat k="Signal" v={c.signal || ""} highlight={c.signal === "HIGH"} variant={variant} />
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ───────────────────────── matrix ───────────────────────── */

export function Matrix({
  roiPrioritization,
  variant = "screen",
  stacked = false,
}: {
  roiPrioritization?: ReportCopy["roi_prioritization"];
  variant?: SectionVariant;
  stacked?: boolean;
}) {
  const points = roiPrioritization?.points?.length ? roiPrioritization.points : [];
  const recommendedTitle = roiPrioritization?.recommended_first_pilot_title || "";
  const recommendedSummary = roiPrioritization?.recommended_first_pilot_summary || "";

  return (
    <Section
      id="matrix"
      index="05"
      eyebrow="ROI Prioritization"
      title={
        <>
          Opportunity <Accent variant={variant}>prioritization</Accent> matrix.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      {variant === "pdf" ? (
        <div className="pdf-matrix-layout card-premium">
          <div className="relative w-full pdf-matrix-chart">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -rotate-90 origin-left text-[9px] uppercase tracking-[0.16em] text-foreground/55 -translate-x-1">
              Business Impact
            </div>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-4 text-[9px] uppercase tracking-[0.16em] text-foreground/55">
              Ease of Deployment
            </div>
            <div className="absolute inset-0 ml-6 mb-6 rounded-xl border border-foreground/10 bg-gradient-to-br from-[rgba(0,87,255,0.04)] to-transparent">
              <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
                <div className="border-r border-b border-foreground/5" />
                <div className="border-b border-foreground/5" />
                <div className="border-r border-foreground/5" />
                <div />
              </div>
              <div className="absolute left-2 top-1 text-[8px] uppercase tracking-[0.16em] text-foreground/40">High Impact</div>
              <div className="absolute right-2 top-1 text-[8px] uppercase tracking-[0.16em] text-[var(--electric)]">Quick Wins</div>
              {points.map((p) => (
                <div
                  key={`${p.index}-${p.label}`}
                  className="absolute -translate-x-1/2 translate-y-1/2"
                  style={{ left: `${p.x ?? 0}%`, bottom: `${p.y ?? 0}%` }}
                >
                  <span
                    className={`block rounded-full ${p.featured ? "h-4 w-4" : "h-2.5 w-2.5"}`}
                    style={{ background: "var(--gradient-signal)" }}
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[9px] font-medium leading-tight max-w-[100px]">
                    <span className="mr-1 text-[var(--electric)] font-semibold">{p.index}</span>
                    {p.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="pdf-matrix-pilot">
            <PdfChip size="xs">Recommended First Pilot</PdfChip>
            <h3 className="mt-2 font-display text-lg font-semibold leading-snug">{recommendedTitle}</h3>
            <p className="mt-2 text-[12px] text-foreground/65 leading-relaxed">{recommendedSummary}</p>
          </div>
        </div>
      ) : (
      <div className="card-premium p-8 md:p-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="relative w-full report-matrix-chart aspect-[4/3]">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -rotate-90 origin-left text-[11px] uppercase tracking-[0.2em] text-foreground/55 -translate-x-2">
                Business Impact →
              </div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-6 text-[11px] uppercase tracking-[0.2em] text-foreground/55">
                Ease of Deployment →
              </div>
              <div className="absolute inset-0 ml-8 mb-8 rounded-xl border border-foreground/10 bg-gradient-to-br from-[rgba(0,87,255,0.04)] to-transparent">
                <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
                  <div className="border-r border-b border-foreground/5" />
                  <div className="border-b border-foreground/5" />
                  <div className="border-r border-foreground/5" />
                  <div />
                </div>
                <div className="absolute left-3 top-2 text-[10px] uppercase tracking-[0.2em] text-foreground/40">High Impact</div>
                <div className="absolute right-3 top-2 text-[10px] uppercase tracking-[0.2em] text-[var(--electric)]">Quick Wins</div>
                <div className="absolute left-3 bottom-2 text-[10px] uppercase tracking-[0.2em] text-foreground/40">Defer</div>
                <div className="absolute right-3 bottom-2 text-[10px] uppercase tracking-[0.2em] text-foreground/40">Easy</div>
                {points.map((p) => (
                  <div
                    key={`${p.index}-${p.label}`}
                    className="absolute -translate-x-1/2 translate-y-1/2"
                    style={{ left: `${p.x ?? 0}%`, bottom: `${p.y ?? 0}%` }}
                  >
                    <div className="relative">
                      {p.featured && (
                        <span
                          className="absolute -inset-3 rounded-full pulse-glow"
                          style={{ background: "radial-gradient(circle, rgba(0,87,255,0.4), transparent 70%)" }}
                        />
                      )}
                      <span
                        className={`relative block rounded-full ${p.featured ? "h-5 w-5" : "h-3.5 w-3.5"}`}
                        style={{ background: "var(--gradient-signal)", boxShadow: "0 0 18px rgba(0,87,255,0.6)" }}
                      />
                      <span className="absolute left-6 top-1/2 -translate-y-1/2 whitespace-nowrap text-[12px] font-medium">
                        <span className="mr-2 text-[var(--electric)] font-semibold">{p.index}</span>
                        {p.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:col-span-4">
            <span className="chip">Recommended First Pilot</span>
            <h3 className="mt-4 font-display text-2xl font-semibold leading-snug">{recommendedTitle}</h3>
            <p className="mt-3 text-[14px] text-foreground/65 leading-relaxed">{recommendedSummary}</p>
          </div>
        </div>
      </div>
      )}
    </Section>
  );
}

/* ───────────────────────── roadmap ───────────────────────── */

const EXECUTION_WEEK_RANGES = ["Weeks 1–2", "Weeks 3–4", "Weeks 5–8", "Weeks 9–12"];

function normalizePhaseLabel(phase: string | undefined, index: number) {
  const text = (phase || "").trim();
  const match = text.match(/^Phase\s+(\d+)\b/i);
  if (match) {
    return `Phase ${String(parseInt(match[1], 10)).padStart(2, "0")}`;
  }
  return `Phase ${String(index + 1).padStart(2, "0")}`;
}

function normalizePhaseWeeks(weeks: string | undefined, index: number) {
  const text = (weeks || "").trim();
  if (/week/i.test(text) && (/[–-]/.test(text) || /\bto\b/i.test(text))) {
    return text;
  }
  return EXECUTION_WEEK_RANGES[index] ?? EXECUTION_WEEK_RANGES[EXECUTION_WEEK_RANGES.length - 1];
}

export function Roadmap({
  phases,
  variant = "screen",
  stacked = false,
}: {
  phases?: ExecutionPhase[];
  variant?: SectionVariant;
  stacked?: boolean;
}) {
  const renderedPhases = phases?.length ? phases : [];

  return (
    <Section
      id="roadmap"
      index="06"
      eyebrow="90-Day Execution"
      title={
        <>
          90-day <Accent variant={variant}>execution</Accent> plan.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      {variant === "pdf" ? (
        <div className="pdf-roadmap-grid roadmap-timeline-grid">
          {renderedPhases.map((p, i) => (
            <div key={p.phase || i} className="pdf-roadmap-phase roadmap-timeline-phase">
              <div className="roadmap-timeline-dot-slot">
                <div className="pdf-roadmap-dot">
                  <span />
                </div>
              </div>
              <div className="pdf-roadmap-card roadmap-timeline-card card-premium">
                <span className="text-[8px] uppercase tracking-[0.18em] text-[var(--electric)] font-medium">
                  {normalizePhaseLabel(p.phase, i)}
                </span>
                <h3 className="mt-1.5 font-display text-sm font-semibold leading-snug">{p.title}</h3>
                <p className="mt-1 text-[10px] text-foreground/55">{normalizePhaseWeeks(p.weeks, i)}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="relative flex-1">
        <div className="roadmap-timeline-connector absolute left-0 right-0 hidden md:block">
          <div
            className="h-px w-full"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(0,87,255,0.4), rgba(10,132,255,0.6), rgba(0,87,255,0.4), transparent)",
            }}
          />
        </div>
        <div className="roadmap-timeline-grid grid grid-cols-1 md:grid-cols-4 gap-6">
          {renderedPhases.map((p, i) => (
            <div key={p.phase || i} className="roadmap-timeline-phase relative">
              <div className="roadmap-timeline-dot-slot">
                <div className="relative flex items-center justify-center">
                  <span
                    className="absolute -inset-3 rounded-full pulse-glow"
                    style={{
                      background: "radial-gradient(circle, rgba(0,87,255,0.45), transparent 70%)",
                      animationDelay: `${i * 0.4}s`,
                    }}
                  />
                  <span
                    className="relative flex h-[22px] w-[22px] items-center justify-center rounded-full"
                    style={{ background: "var(--gradient-signal)", boxShadow: "0 0 16px rgba(0,87,255,0.6)" }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                  </span>
                </div>
              </div>
              <div className="roadmap-timeline-card card-premium text-center p-6">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--electric)] font-medium">
                  {normalizePhaseLabel(p.phase, i)}
                </span>
                <h3 className="mt-2 font-display text-lg font-semibold">{p.title}</h3>
                <p className="mt-1 text-[12px] text-foreground/55">{normalizePhaseWeeks(p.weeks, i)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </Section>
  );
}

/* ───────────────────────── outcomes ───────────────────────── */

export function Outcomes({
  expectedOutcomes,
  variant = "screen",
  compactSummary = false,
  stacked = false,
}: {
  expectedOutcomes?: ReportCopy["expected_outcomes"];
  variant?: SectionVariant;
  compactSummary?: boolean;
  stacked?: boolean;
}) {
  const items = expectedOutcomes?.items?.length ? expectedOutcomes.items : [];
  const summary = expectedOutcomes?.summary || "";

  return (
    <Section
      id="outcomes"
      index="07"
      eyebrow="Expected Outcomes"
      title={
        <>
          Projected <Accent variant={variant}>business outcomes</Accent>.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      <div className={variant === "pdf" ? "pdf-outcomes-grid" : "grid grid-cols-2 lg:grid-cols-4 gap-5"}>
        {items.map((m) => (
          <div key={m.label} className="card-premium p-7">
            <div className="flex items-center gap-2 text-[var(--electric)]">
              {m.direction === "down" ? <ArrowDownRight className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
              <span className="text-[11px] uppercase tracking-[0.2em] text-foreground/55">Projected</span>
            </div>
            <p className={`mt-4 font-display font-semibold tracking-tight ${variant === "pdf" ? "text-3xl" : "text-4xl md:text-5xl"}`}>
              <Accent variant={variant}>{m.value}</Accent>
            </p>
            <p className="mt-2 text-[13px] text-foreground/65">{m.label}</p>
          </div>
        ))}
      </div>
      {summary ? (
        <p
          className={`max-w-3xl text-center mx-auto font-display text-foreground/65 leading-snug ${
            variant === "pdf" ? "pdf-outcomes-summary" : compactSummary ? "mt-4 text-base" : "mt-10 text-lg md:text-xl"
          }`}
        >
          {summary}
        </p>
      ) : null}
    </Section>
  );
}

/* ───────────────────────── positioning ───────────────────────── */

export function Positioning({
  industryPositioning,
  variant = "screen",
  stacked = false,
}: {
  industryPositioning?: ReportCopy["industry_positioning"];
  variant?: SectionVariant;
  stacked?: boolean;
}) {
  const stages = ["Beginner", "Exploring", "Scaling", "Operationalizing", "Leading"];
  const current =
    typeof industryPositioning?.current_index === "number"
      ? Math.max(0, Math.min(4, Math.round(industryPositioning.current_index)))
      : 0;

  return (
    <Section
      id="positioning"
      index="08"
      eyebrow="Industry Positioning"
      title={
        <>
          AI maturity <Accent variant={variant}>position</Accent> relative to peers.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      <div className={`card-premium ${variant === "pdf" ? "p-6" : "p-8 md:p-12"}`}>
        <div className="relative report-positioning-track">
          <div
            className="report-positioning-track__line h-1 rounded-full"
            style={{ background: "linear-gradient(90deg, rgba(7,17,31,0.06), rgba(0,87,255,0.4) 75%, rgba(10,132,255,0.8))" }}
          />
          <div className="mt-6 grid grid-cols-5 gap-2">
            {stages.map((s, i) => (
              <div key={s} className="relative flex flex-col items-center text-center">
                <div className="absolute -top-[34px]">
                  <span
                    className={`block rounded-full ${i === current ? "h-4 w-4" : "h-2.5 w-2.5"}`}
                    style={{
                      background: i <= current ? "var(--gradient-signal)" : "rgba(7,17,31,0.15)",
                      boxShadow: i === current ? "0 0 18px rgba(0,87,255,0.65)" : "none",
                    }}
                  />
                </div>
                <p
                  className={`text-[11px] uppercase tracking-[0.18em] ${i === current ? "text-[var(--electric)] font-semibold" : "text-foreground/55"}`}
                >
                  {s}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${variant === "pdf" ? "mt-6" : "mt-12"}`}>
          <div>
            {variant === "pdf" ? (
              <PdfChip size="xs">Current Position</PdfChip>
            ) : (
              <span className="chip">Current Position</span>
            )}
            <p className="mt-3 font-display text-2xl font-semibold">{industryPositioning?.current_position_title || ""}</p>
            <p className="mt-2 text-[14px] text-foreground/60">{industryPositioning?.current_position_body || ""}</p>
          </div>
          <div>
            {variant === "pdf" ? (
              <PdfChip size="xs">Next Maturity Unlock</PdfChip>
            ) : (
              <span className="chip">Next Maturity Unlock</span>
            )}
            <p className="mt-3 font-display text-2xl font-semibold">{industryPositioning?.next_maturity_unlock_title || ""}</p>
            <p className="mt-2 text-[14px] text-foreground/60">{industryPositioning?.next_maturity_unlock_body || ""}</p>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ───────────────────────── risks ───────────────────────── */

export function Risks({
  risks,
  variant = "screen",
  spacious = false,
  stacked = false,
}: {
  risks?: StrategicRiskItem[];
  variant?: SectionVariant;
  spacious?: boolean;
  stacked?: boolean;
}) {
  const renderedRisks = (risks || [])
    .map((risk) => ({
      t: risk.title || "",
      s: typeof risk.severity === "number" ? Math.max(1, Math.min(3, Math.round(risk.severity))) : 2,
    }))
    .filter((risk) => Boolean(risk.t));

  return (
    <Section
      id="risks"
      index="09"
      eyebrow="Strategic Risks"
      title={
        <>
          Risks that may <Accent variant={variant}>limit</Accent> AI return.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
      className={spacious && !stacked ? "flex-1" : ""}
    >
      <div
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${
          spacious && !stacked ? "pdf-risks-grid flex-1 content-stretch" : ""
        }`}
      >
        {renderedRisks.map((r) => (
          <div key={r.t} className="card-premium p-6">
            <div className="flex items-start justify-between">
              <ShieldAlert className="h-5 w-5 text-[var(--electric)]" />
              <div className="flex gap-1">
                {[1, 2, 3].map((n) => (
                  <span
                    key={n}
                    className={`h-1.5 w-5 rounded-full ${n <= r.s ? "" : "bg-foreground/10"}`}
                    style={n <= r.s ? { background: "var(--gradient-signal)" } : {}}
                  />
                ))}
              </div>
            </div>
            <p className="mt-5 font-display text-lg font-semibold leading-snug">{r.t}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ───────────────────────── framework ───────────────────────── */

export function Framework({
  variant = "screen",
  diagramSize = 460,
  stacked = false,
}: {
  variant?: SectionVariant;
  diagramSize?: number;
  stacked?: boolean;
}) {
  const nodes = [
    { label: "DATA", icon: Database },
    { label: "PROCESS", icon: Workflow },
    { label: "PEOPLE", icon: Users },
    { label: "TECHNOLOGY", icon: Cpu },
    { label: "STRATEGY", icon: Compass },
  ];
  const size = diagramSize;
  const r = Math.round(diagramSize * 0.37);

  return (
    <Section
      id="framework"
      index="10"
      eyebrow="The Signal AI Framework"
      title={
        <>
          Five-pillar <Accent variant={variant}>assessment</Accent> framework.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
      className={stacked ? "" : "flex-1"}
    >
      <div className={`card-premium report-framework-card ${variant === "pdf" ? "p-4" : "p-8 md:p-12"}`}>
        <div className="report-framework-wrap flex justify-center">
          <div className="relative" style={{ width: size, height: size }}>
            <div
              className="absolute inset-0 rounded-full"
              style={{ background: "radial-gradient(circle, rgba(0,87,255,0.18), transparent 65%)" }}
            />
            <svg className="absolute inset-0" viewBox={`0 0 ${size} ${size}`}>
              <defs>
                <linearGradient id="loop" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#0057FF" stopOpacity="0.7" />
                  <stop offset="100%" stopColor="#0A84FF" stopOpacity="0.7" />
                </linearGradient>
              </defs>
              <circle cx={size / 2} cy={size / 2} r={r} stroke="url(#loop)" strokeWidth="1" fill="none" strokeDasharray="2 6" />
              <circle cx={size / 2} cy={size / 2} r={r - 40} stroke="rgba(7,17,31,0.06)" strokeWidth="1" fill="none" />
            </svg>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] text-foreground/55">Signal AI</p>
              <p className="font-display text-lg font-semibold">Assessment Cycle</p>
            </div>
            {nodes.map((n, i) => {
              const angle = (i / nodes.length) * Math.PI * 2 - Math.PI / 2;
              const x = Math.cos(angle) * r + size / 2;
              const y = Math.sin(angle) * r + size / 2;
              return (
                <div key={n.label} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: x, top: y }}>
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white border border-foreground/8 shadow-[0_8px_24px_rgba(0,87,255,0.18)]">
                      <n.icon className="h-5 w-5 text-[var(--electric)]" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">{n.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ───────────────────────── next 90 ───────────────────────── */

export function NextNinety({
  sectionEyebrow,
  sectionTitle,
  items,
  variant = "screen",
  stacked = false,
  showHeader = true,
  continuation = false,
  itemOffset = 0,
}: {
  sectionEyebrow?: string;
  sectionTitle?: string;
  items?: RecommendedImmediateActionItem[];
  variant?: SectionVariant;
  stacked?: boolean;
  showHeader?: boolean;
  continuation?: boolean;
  itemOffset?: number;
}) {
  const renderedItems = (items || [])
    .map((item) => ({
      w: item.time_label || "",
      t: item.title || "",
      d: item.description || "",
    }))
    .filter((item) => Boolean(item.w && item.t && item.d));

  const eyebrow =
    variant === "pdf"
      ? "Immediate Actions Required"
      : sectionEyebrow || "Recommended Immediate Actions";

  const actionsList = (
    <div
      className={`report-immediate-actions-card card-premium ${
        variant === "pdf" ? "report-immediate-actions-card--pdf p-2" : "p-2 md:p-3 overflow-hidden"
      }`}
    >
      <ol className="divide-y divide-foreground/5">
        {renderedItems.map((i, idx) =>
          variant === "pdf" ? (
            <li key={`${i.w}-${idx}`} className="report-immediate-action-row report-immediate-action-row--pdf">
              <span className="report-immediate-action-row__index">
                {String(itemOffset + idx + 1).padStart(2, "0")}
              </span>
              <div className="report-immediate-action-row__badge">
                <PdfChip size="xs">{i.w}</PdfChip>
              </div>
              <div className="report-immediate-action-row__content">
                <p className="report-immediate-action-row__title">{i.t}</p>
                <p className="report-immediate-action-row__desc">{i.d}</p>
              </div>
            </li>
          ) : (
            <li
              key={`${i.w}-${idx}`}
              className="report-immediate-action-row flex items-center gap-6 px-6 py-5 group hover:bg-[rgba(0,87,255,0.03)] transition"
            >
              <span className="font-display w-8 shrink-0 self-start pt-1 text-sm tabular-nums text-foreground/45">
                {String(itemOffset + idx + 1).padStart(2, "0")}
              </span>
              <span className="chip shrink-0 self-start">{i.w}</span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg font-medium">{i.t}</p>
                <p className="mt-2 text-[14px] leading-relaxed text-foreground/65">{i.d}</p>
              </div>
            </li>
          ),
        )}
      </ol>
    </div>
  );

  if (variant === "pdf" && continuation) {
    return (
      <div className="pdf-section-block pdf-section-block--stacked pdf-next-ninety-continuation">
        <div className="pdf-section-block__body">{actionsList}</div>
      </div>
    );
  }

  return (
    <Section
      id={showHeader ? "next90" : undefined}
      index={showHeader ? "11" : undefined}
      eyebrow={showHeader ? eyebrow : undefined}
      title={
        showHeader
          ? sectionTitle || (
              <>
                Recommended actions for the next <Accent variant={variant}>90 days</Accent>.
              </>
            )
          : undefined
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      {actionsList}
    </Section>
  );
}

/* ───────────────────────── about ───────────────────────── */

export function AboutGowitek({ variant = "screen", stacked = false }: { variant?: SectionVariant; stacked?: boolean }) {
  const pillars = [
    { h: "Business-Led Delivery", p: "Execution aligned to operational priorities and measurable outcomes.", icon: Compass },
    { h: "Practical AI Systems", p: "Use cases designed for production deployment, not proof-of-concept only.", icon: Cpu },
    { h: "Measured Outcomes", p: "Progress tracked against defined KPIs and business targets.", icon: TrendingUp },
  ];
  const products = [
    { n: "Signal AI", d: "AI readiness assessment and prioritization." },
    { n: "Nexus AI", d: "Operational decision support across systems." },
    { n: "ServitizeIQ", d: "Service performance analytics and reporting." },
  ];

  return (
    <Section
      id="about"
      index="12"
      eyebrow="About Gowitek"
      title={
        <>
          About <Accent variant={variant}>Gowitek</Accent>.
        </>
      }
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      <div className="report-about-pillars grid grid-cols-1 md:grid-cols-3 gap-5">
        {pillars.map((p) => (
          <div key={p.h} className="card-premium p-7">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "var(--gradient-signal)" }}>
              <p.icon className="h-5 w-5 text-white" />
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold">{p.h}</h3>
            <p className="mt-2 text-[14px] text-foreground/65">{p.p}</p>
          </div>
        ))}
      </div>
      <div className={`report-about-products grid grid-cols-1 md:grid-cols-3 gap-5 ${variant === "pdf" ? "mt-5" : "mt-10"}`}>
        {products.map((pr) => (
          <div key={pr.n} className="card-premium p-6 flex items-center gap-4">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: "var(--gradient-signal)", boxShadow: "0 0 12px rgba(0,87,255,0.6)" }} />
            <div>
              <p className="font-display font-semibold">{pr.n}</p>
              <p className="text-[12px] text-foreground/55">{pr.d}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ───────────────────────── signal inputs ───────────────────────── */

export function SignalInputs({
  assessmentSignalInputs,
  variant = "screen",
  stacked = false,
}: {
  assessmentSignalInputs?: AssessmentSignalInputsSection;
  variant?: SectionVariant;
  stacked?: boolean;
}) {
  const groups = [
    { tone: "strong", h: "Strong Signals", items: assessmentSignalInputs?.strong_signals || [] },
    { tone: "moderate", h: "Moderate Signals", items: assessmentSignalInputs?.moderate_signals || [] },
    { tone: "watch", h: "Watch Areas", items: assessmentSignalInputs?.watch_areas || [] },
  ];

  const dot = (t: string) =>
    t === "strong"
      ? "var(--gradient-signal)"
      : t === "moderate"
        ? "linear-gradient(135deg,#0A84FF,#9bb6ff)"
        : "linear-gradient(135deg,#94a3b8,#cbd5e1)";

  return (
    <Section
      id="inputs"
      index="13"
      eyebrow={assessmentSignalInputs?.section_eyebrow || "Assessment Signal Inputs"}
      title={assessmentSignalInputs?.section_title || "Assessment inputs by signal strength."}
      variant={variant}
      stacked={stacked}
      compact={stacked}
    >
      <div className="report-inputs-grid grid grid-cols-1 md:grid-cols-3 gap-5">
        {groups.map((g) => (
          <div key={g.h} className="card-premium p-7">
            <div className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: dot(g.tone) }} />
              <span className="text-[11px] uppercase tracking-[0.2em] text-foreground/55">{g.h}</span>
            </div>
            <ul className="mt-5 space-y-3">
              {g.items.map((it) => (
                <li key={it} className="flex items-start gap-2 text-[14px]">
                  <span className="mt-2 h-1 w-1 rounded-full bg-foreground/30" />
                  <span className="text-foreground/80">{it}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  );
}
